import { Fragment, type ReactNode, useMemo, useRef } from 'react';
import { Picker, type PickerProps, type PickerRef } from './Picker';
import { cn } from './react';
import type { FormControlProps } from './types';

export type SelectOption<V> = {
  key: string | number;
  value: V;
  disabled?: boolean;
  hidden?: boolean;
  label?: (target: 'option' | 'value') => ReactNode;
};

export type SelectProps<
  M extends boolean,
  V,
  O = SelectOption<V>,
> = FormControlProps<M extends true ? V[] : V> &
  PickerProps & {
    multiple?: M;
    options: O[];
    placeholder?: string;
  };

export const Select = <M extends boolean = false, V = unknown>({
  value,
  options,
  multiple,
  onChange,
  placeholder,
  required,
  ...pickerProps
}: SelectProps<M, V>) => {
  const ref = useRef<PickerRef>(null);
  const valueList = Array.isArray(value)
    ? value
    : typeof value !== 'undefined'
      ? [value]
      : [];
  const optionsMap = useMemo(() => {
    const map = new Map<V, SelectOption<V>>();
    options.forEach((opt) => {
      map.set(opt.value, opt);
    });
    return map;
  }, [options]);

  return (
    <Picker
      {...pickerProps}
      ref={ref}
      dialogTitle={placeholder}
      root={
        <>
          {valueList.map((val, i) => (
            <Fragment key={optionsMap.get(val)?.key ?? i.toString()}>
              {optionsMap.get(val)?.label?.('value')}
            </Fragment>
          ))}
          {valueList.length === 0 && (
            <p className="text-content-primary/25 text-sm">{placeholder}</p>
          )}
        </>
      }
      modal={options.map((opt) => {
        const isChecked = valueList.includes(opt.value as never);
        return (
          <button
            key={opt.key}
            type="button"
            className={cn(
              'block h-12 w-full rounded-none p-3 enabled:cursor-pointer transition-all text-start',
              isChecked
                ? 'bg-background-hover/30'
                : opt.disabled
                  ? 'bg-background-disabled/30'
                  : 'hover:bg-background-hover/20',
            )}
            disabled={opt.disabled}
            onClick={() => {
              if (multiple) {
                onChange?.(
                  (isChecked
                    ? valueList.filter((x) => x !== opt.value)
                    : [...valueList, opt.value]) as never,
                );
              } else {
                onChange?.(
                  (isChecked && !required ? undefined : opt.value) as never,
                );
                ref.current?.[1](false);
              }
            }}
          >
            {opt.label?.('option')}
          </button>
        );
      })}
    />
  );
};
